import { cn } from '@repo/tailwind-config/tailwind.functions'
import { TailwindColor, tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { SVGFC } from '@repo/toolkit/types'
import { PropsWithChildren, useRef, useState } from 'react'

import Loader from './loader'
import Typography from './typography'

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'outline'
  | 'destructive'
  | 'ghost'
  | 'link'
  | 'icon'
  | 'highlighted'
  | 'disabled'
  | 'success'
  | 'warning'
  | 'outline-fushia'
  | 'orange'
  | 'outline-black'

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Is this the principal call to action on the page?
   */
  variant?: ButtonVariant

  /**
   * Button contents
   */
  label?: string

  /**
   * Icon to display on the button
   */
  icon?: SVGFC

  /**
   * Position icon to the right of the label
   */
  iconToTheRight?: boolean

  /**
   * Loading state of the button
   */
  loading?: boolean

  /**
   * Additional class names for icon
   */
  iconColor?: TailwindColor

  iconSize?: number

  /**
   * Size of the button
   */
  size?: 'xsmall' | 'small' | 'medium'
}

export const ButtonColorMapping: Record<
  ButtonVariant,
  { DEFAULT: TailwindColor; hover: TailwindColor }
> = {
  primary: {
    DEFAULT: tailwindColors.white.DEFAULT,
    hover: tailwindColors.white.DEFAULT
  },
  secondary: {
    DEFAULT: tailwindColors.primary.DEFAULT,
    hover: tailwindColors.white.DEFAULT
  },
  outline: {
    DEFAULT: tailwindColors.primary.DEFAULT,
    hover: tailwindColors.white.DEFAULT
  },
  destructive: {
    DEFAULT: tailwindColors.red.DEFAULT,
    hover: tailwindColors.white.DEFAULT
  },
  ghost: {
    DEFAULT: tailwindColors.primary.DEFAULT,
    hover: tailwindColors.white.DEFAULT
  },
  icon: {
    DEFAULT: tailwindColors.white.DEFAULT,
    hover: tailwindColors.white.DEFAULT
  },
  highlighted: {
    DEFAULT: tailwindColors.secondary.DEFAULT,
    hover: tailwindColors.secondary.hover
  },
  tertiary: {
    DEFAULT: tailwindColors.white.active,
    hover: tailwindColors.white.active
  },
  disabled: {
    DEFAULT: tailwindColors.grey.light.active,
    hover: tailwindColors.grey.light.active
  },
  link: {
    DEFAULT: tailwindColors.primary.DEFAULT,
    hover: tailwindColors.primary.DEFAULT
  },
  success: {
    DEFAULT: tailwindColors.green.DEFAULT,
    hover: tailwindColors.green.light.hover
  },
  warning: {
    DEFAULT: tailwindColors.red.light.DEFAULT,
    hover: tailwindColors.red.light.hover
  },
  orange: {
    DEFAULT: tailwindColors.orange.DEFAULT,
    hover: tailwindColors.orange.hover
  },
  'outline-fushia': {
    DEFAULT: tailwindColors.fushia.DEFAULT,
    hover: tailwindColors.white.DEFAULT
  },
  'outline-black': {
    DEFAULT: tailwindColors.black.DEFAULT,
    hover: tailwindColors.black.DEFAULT
  }
}

/**
 * Primary UI component for user interaction
 */
const Button = ({
  variant = 'primary',
  label,
  icon,
  className,
  iconColor: iconColorProp,
  loading,
  size = 'medium',
  iconSize = 20,
  children,
  iconToTheRight,
  ...props
}: PropsWithChildren<ButtonProps>) => {
  const hoverRef = useRef<HTMLButtonElement>(null)
  return (
    <button
      ref={hoverRef}
      className={cn(
        'rounded flex items-center space-x-1 transition-colors duration-200 justify-center ',
        {
          'px-4 h-[40px]  mobile:px-2': size === 'medium',
          'px-3 h-[34px]': size === 'small',
          'p-1': size === 'xsmall',
          'bg-primary text-white border border-primary hover:bg-primary-hover':
            variant === 'primary',
          'bg-white text-primary border border-grey-light hover:bg-primary hover:text-white hover:border-primary':
            variant === 'secondary',
          'bg-white-active text-grey': variant === 'tertiary',
          'bg-transparent text-primary border border-primary hover:bg-primary hover:text-white':
            variant === 'outline',
          'bg-transparent text-fushia border border-fushia hover:bg-fushia hover:text-white':
            variant === 'outline-fushia',
          'bg-transparent text-black border border-grey-light hover:bg-grey-light-hover hover:text-black':
            variant === 'outline-black',
          'bg-red-light text-red border border-red-light hover:bg-red hover:text-white hover:border-red':
            variant === 'destructive',
          'bg-primary-light text-primary border border-primary-light hover:bg-primary hover:text-white hover:border-primary mobile-ghost-button':
            variant === 'ghost',
          'bg-primary rounded-full p-3': variant === 'icon',
          'bg-secondary-light text-secondary border border-secondary-light-active hover:bg-secondary-light-hover hover:text-secondary-active hover:border-seondary':
            variant === 'highlighted',
          'pl-3': !!icon && variant !== 'icon' && label,
          'cursor-not-allowed bg-grey-light hover:bg-grey-light !text-grey-lighter hover:text-grey-lighter border-0':
            props.disabled,
          'bg-orange-light text-orange hover:bg-orange-hover hover:text-orange-light':
            variant === 'orange',
          'bg-grey-light !text-grey-light-active border-0': variant === 'disabled',
          'text-primary border-0 p-0': variant === 'link',
          'bg-green-light text-green border border-green-light hover:bg-green hover:text-white hover:border-green':
            variant === 'success',
          'bg-red-light border-red-light hover:bg-red-light': variant === 'warning',
          'bg-transparent hover:bg-transparent': variant === 'link' && props.disabled,
          'flex-row-reverse': iconToTheRight
        },
        className
      )}
      {...props}
    >
      {loading ? (
        <Loader />
      ) : (
        <>
          {icon?.({
            color: props.disabled
              ? tailwindColors.grey.lighter
              : (iconColorProp ?? 'currentColor'),
            height: size === 'xsmall' ? '14px' : `${iconSize}px`,
            width: size === 'xsmall' ? '14px' : `${iconSize}px`
          })}
          {variant !== 'icon' && label && <Typography variant="label-medium">{label}</Typography>}
          {children}
        </>
      )}
    </button>
  )
}

export default Button

type LoadingButtonProps = ButtonProps & {
  onClick: () => Promise<void>
}

Button.Loading = ({ onClick, ...props }: PropsWithChildren<LoadingButtonProps>) => {
  const [loading, setLoading] = useState(false)

  return (
    <Button
      {...props}
      loading={loading}
      onClick={async () => {
        setLoading(true)
        await onClick?.()
        setLoading(false)
      }}
    />
  )
}
